import { html, type TemplateResult } from "lit-html";
import type { AppState } from "../data/store";
import type { BuyerRow } from "../data/types";
import { store } from "../data/store";
import { renderAccountTabs } from "./AccountTabs";
import { formatRelative } from "../utils/time";
import { activeBuyers, applyAccountFilter, dollars, initials, computeClosedStats } from "../utils/format";

/**
 * Closed Sales cube — paid deals only, with a revenue summary banner
 * at the top of the body and the same row layout as the Pipeline cube.
 */
export function renderClosedSalesCube(state: AppState): TemplateResult {
  const rows = applyAccountFilter(activeBuyers(state.buyers), state.selectedAccountId)
    .filter((b) => b.conv_status === "paid");
  const stats = computeClosedStats(rows);

  return html`
    <div class="cube cube--closed">
      <div class="cube__head cube-shell__drag">
        <span class="cube__title">Closed sales</span>
        <span class="cube__count">${rows.length}</span>
        <div class="cube__head-tabs">
          ${renderAccountTabs(state.accounts, state.selectedAccountId)}
        </div>
      </div>
      <div class="cube__body">
        <div class="revbar">
          <div class="revbar__cell revbar__cell--primary">
            <div class="revbar__num">${dollars(stats.totalCents)}</div>
            <div class="revbar__lbl">revenue</div>
          </div>
          <div class="revbar__cell">
            <div class="revbar__num">${stats.totalTickets}</div>
            <div class="revbar__lbl">tickets</div>
          </div>
          <div class="revbar__cell">
            <div class="revbar__num">${stats.dealCount}</div>
            <div class="revbar__lbl">deals</div>
          </div>
          <div class="revbar__cell">
            <div class="revbar__num">${stats.avgPerTicketCents != null ? dollars(stats.avgPerTicketCents) : "—"}</div>
            <div class="revbar__lbl">avg / ticket</div>
          </div>
        </div>
        ${rows.length === 0
          ? html`<div class="empty">No closed sales yet. Hit "Mark as Paid" inside a buyer to land one here.</div>`
          : html`
            <div class="buyers-row buyers-row--head">
              <span>Buyer</span>
              <span>Account</span>
              <span>Listing</span>
              <span>Qty</span>
              <span>Total</span>
              <span>Per ticket</span>
              <span>Closed</span>
            </div>
            ${rows.map(renderRow)}
          `}
      </div>
    </div>
  `;
}

function renderRow(b: BuyerRow): TemplateResult {
  const per = b.conv_tickets_count && b.conv_tickets_count > 0 && b.conv_agreed_price_cents != null
    ? b.conv_agreed_price_cents / b.conv_tickets_count
    : null;
  return html`
    <div class="buyers-row buyers-row--clickable buyers-row--paid"
         @click=${() => { if (b.conv_id) store.openEditor(b.conv_id); }}>
      <span class="buyer-name">
        <span class="buyer-avatar buyer-avatar--paid">${initials(b.display_name)}</span>
        <span>${b.display_name}</span>
      </span>
      <span class="buyer-meta">${b.account_username ?? html`<span class="muted">—</span>`}</span>
      <span class="listing-cell">
        <span class="listing-title">${b.listing_title ?? html`<span class="muted">—</span>`}</span>
      </span>
      <span class="qty-cell">${b.conv_tickets_count ?? html`<span class="muted">—</span>`}</span>
      <span class="price-cell price-cell--agreed">
        ${b.conv_agreed_price_cents != null ? dollars(b.conv_agreed_price_cents) : html`<span class="muted">—</span>`}
      </span>
      <span class="price-cell">${per != null ? dollars(per) : html`<span class="muted">—</span>`}</span>
      <span class="time-cell">
        ${b.conv_last_activity_at ? formatRelative(b.conv_last_activity_at) : html`<span class="muted">—</span>`}
      </span>
    </div>
  `;
}
